import { useEffect, useState, useCallback, useRef } from "react";
import QRCode from "qrcode";
import { Money } from "./SaudiRiyal";
import { toast } from "sonner";
import { Package, Plus, Pencil, Trash2, QrCode, AlertTriangle, History, Search, Loader2, Printer, Car, Upload, Download, FileSpreadsheet, Warehouse, PackagePlus, Settings2, ArrowLeftRight } from "lucide-react";
import { Button } from "./ui/button";
import { BulkTools } from "./BulkTools";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Badge } from "./ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "./ui/dialog";
import { Tabs, TabsList, TabsTrigger } from "./ui/tabs";
import { api } from "../lib/api";
import { WarehousesDialog } from "./WarehousesDialog";

const money = (n) => <Money value={n} />;
const emptyForm = { name: "", sku: "", quantity: "", min_alert: "5", price: "", category: "", location: "" };

const RestockDialog = ({ part, onClose, onDone }) => {
  const [qty, setQty] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (part) setQty(""); }, [part]);
  const save = async () => {
    const n = Number(qty);
    if (!n || n <= 0) { toast.warning("أدخل كمية صحيحة"); return; }
    setBusy(true);
    try { await api.restockSparePart(part.id, n); toast.success("تم إدخال الكمية"); onDone(); onClose(); }
    catch (e) { toast.error(e?.response?.data?.detail || "تعذّر الإدخال"); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={!!part} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-sm font-body" dir="rtl" data-testid="restock-dialog">
        <DialogHeader className="text-right">
          <DialogTitle className="font-heading text-lg">إدخال كمية — {part?.name}</DialogTitle>
          <DialogDescription>الكمية الحالية: {part?.quantity}</DialogDescription>
        </DialogHeader>
        <div className="space-y-1.5">
          <Label>الكمية المُضافة</Label>
          <Input data-testid="restock-qty" type="number" dir="ltr" value={qty}
            onChange={(e) => setQty(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") save(); }} autoFocus />
        </div>
        <DialogFooter className="flex-row-reverse gap-2 sm:justify-start">
          <Button data-testid="restock-save-btn" disabled={busy} onClick={save} className="bg-emerald-600 hover:bg-emerald-700 gap-1.5">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <PackagePlus className="h-4 w-4" />} إدخال
          </Button>
          <Button variant="outline" onClick={onClose}>إلغاء</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const TransferDialog = ({ part, branches, onClose, onDone }) => {
  const [toWh, setToWh] = useState("");
  const [qty, setQty] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (part) { setToWh((branches[0] || {}).id || ""); setQty(""); } }, [part, branches]);
  const save = async () => {
    const n = Number(qty);
    if (!toWh) { toast.warning("اختر الفرع المستلِم"); return; }
    if (!n || n <= 0) { toast.warning("أدخل كمية صحيحة"); return; }
    setBusy(true);
    try { const r = await api.transferStock({ part_id: part.id, to_warehouse_id: toWh, quantity: n }); toast.success(`تم تحويل ${n} إلى ${r.to_warehouse}`); onDone(); onClose(); }
    catch (e) { toast.error(e?.response?.data?.detail || "تعذّر التحويل"); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={!!part} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-sm font-body" dir="rtl" data-testid="transfer-dialog">
        <DialogHeader className="text-right">
          <DialogTitle className="font-heading text-lg">تحويل — {part?.name}</DialogTitle>
          <DialogDescription>المتوفر في الرئيسي: {part?.quantity}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label>الفرع المستلِم</Label>
            {branches.length === 0 ? (
              <p className="text-xs text-slate-400">لا توجد فروع — أنشئ فرعاً من «إدارة المستودعات».</p>
            ) : (
              <select data-testid="transfer-target" value={toWh} onChange={(e) => setToWh(e.target.value)}
                className="h-9 w-full rounded-md border border-slate-200 bg-white px-3 text-sm">
                {branches.map((b) => <option key={b.id} value={b.id}>{b.name}{b.city ? ` — ${b.city}` : ""}</option>)}
              </select>
            )}
          </div>
          <div className="space-y-1.5">
            <Label>الكمية المحوّلة</Label>
            <Input data-testid="transfer-qty" type="number" dir="ltr" value={qty} onChange={(e) => setQty(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") save(); }} />
          </div>
        </div>
        <DialogFooter className="flex-row-reverse gap-2 sm:justify-start">
          <Button data-testid="transfer-save-btn" disabled={busy || branches.length === 0} onClick={save} className="bg-blue-600 hover:bg-blue-700 gap-1.5">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowLeftRight className="h-4 w-4" />} تحويل
          </Button>
          <Button variant="outline" onClick={onClose}>إلغاء</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const PartDialog = ({ open, onOpenChange, initial, onSaved, warehouseId }) => {
  const [form, setForm] = useState(emptyForm);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (open) setForm(initial ? {
      name: initial.name || "", sku: initial.sku || "", quantity: String(initial.quantity ?? ""),
      min_alert: String(initial.min_alert ?? "5"), price: String(initial.price ?? ""),
      category: initial.category || "", location: initial.location || "",
    } : emptyForm);
  }, [open, initial]);
  const save = async () => {
    if (!form.name.trim()) { toast.warning("أدخل اسم القطعة"); return; }
    setBusy(true);
    try {
      const payload = {
        name: form.name, sku: form.sku, quantity: Number(form.quantity) || 0,
        min_alert: Number(form.min_alert === "" ? 5 : form.min_alert) || 0, price: Number(form.price) || 0,
        category: form.category, location: form.location,
        warehouse_id: warehouseId || null,
      };
      if (initial) await api.updateSparePart(initial.id, payload);
      else await api.createSparePart(payload);
      toast.success(initial ? "تم تحديث القطعة" : "تمت إضافة القطعة");
      onOpenChange(false); onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "تعذّر الحفظ"); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg font-body max-h-[90vh] overflow-y-auto" dir="rtl" data-testid="part-dialog">
        <DialogHeader className="text-right">
          <DialogTitle className="font-heading text-xl">{initial ? "تعديل قطعة غيار" : "إضافة قطعة غيار"}</DialogTitle>
          <DialogDescription className="text-right">اترك حقل الرقم (SKU) فارغاً ليُنشأ تلقائياً.</DialogDescription>
        </DialogHeader>
        <div className="grid sm:grid-cols-2 gap-3">
          <div className="space-y-1.5 sm:col-span-2"><Label>اسم القطعة</Label>
            <Input data-testid="part-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
          <div className="space-y-1.5"><Label>الرقم / SKU</Label>
            <Input data-testid="part-sku" dir="ltr" placeholder="تلقائي" value={form.sku} onChange={(e) => setForm({ ...form, sku: e.target.value })} /></div>
          <div className="space-y-1.5"><Label>الكمية الحالية</Label>
            <Input data-testid="part-qty" type="number" dir="ltr" value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} /></div>
          <div className="space-y-1.5"><Label>حد التنبيه للنقص</Label>
            <Input data-testid="part-min" type="number" dir="ltr" value={form.min_alert} onChange={(e) => setForm({ ...form, min_alert: e.target.value })} /></div>
          <div className="space-y-1.5"><Label>السعر (اختياري)</Label>
            <Input data-testid="part-price" type="number" dir="ltr" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} /></div>
          <div className="space-y-1.5"><Label>التصنيف</Label>
            <Input data-testid="part-category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} /></div>
          <div className="space-y-1.5"><Label>مكان التخزين</Label>
            <Input data-testid="part-location" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} /></div>
        </div>
        <DialogFooter className="flex-row-reverse gap-2 sm:justify-start">
          <Button data-testid="part-save-btn" disabled={busy} onClick={save} className="bg-blue-600 hover:bg-blue-700 gap-1.5">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} حفظ
          </Button>
          <Button variant="outline" onClick={() => onOpenChange(false)}>إلغاء</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const QrDialog = ({ part, onClose }) => {
  const [dataUrl, setDataUrl] = useState("");
  const [printing, setPrinting] = useState(false);
  const scanUrl = part ? `${window.location.origin}/scan/${part.id}` : "";
  useEffect(() => {
    if (part) QRCode.toDataURL(scanUrl, { width: 320, margin: 1 }).then(setDataUrl).catch(() => {});
  }, [part, scanUrl]);
  const printLabel = async () => {
    if (!dataUrl) return;
    setPrinting(true);
    let tr = { ar: part.name, en: part.name, ur: part.name };
    try { tr = await api.translateSparePart(part.id); } catch (e) { /* fallback to name */ }
    setPrinting(false);
    const esc = (s) => String(s || "").replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c]));
    const w = window.open("", "_blank", "width=400,height=560");
    if (!w) { toast.error("فعّل النوافذ المنبثقة للطباعة"); return; }
    w.document.write(`<html dir="rtl"><head><style>@font-face{font-family:SaudiRiyal;src:url(/fonts/riyal.woff2) format("woff2");unicode-range:U+20C1}</style><title>ملصق ${esc(part.sku)}</title>
      <style>body{font-family:SaudiRiyal,Arial;text-align:center;padding:16px;margin:0}
      h2{margin:6px 0;font-size:18px}h3{margin:2px 0;font-size:15px;color:#222;font-weight:normal}
      p{margin:2px 0;color:#444;font-size:13px}
      img{width:260px;height:260px}</style></head><body>
      <img src="${dataUrl}"/>
      <h2>${esc(tr.ar)}</h2>
      <h3 dir="ltr">${esc(tr.en)}</h3>
      <h3>${esc(tr.ur)}</h3>
      <p>SKU: ${esc(part.sku)}</p>
      <p>امسح الرمز لتسجيل صرف القطعة</p>
      <script>window.onload=function(){setTimeout(function(){window.print();},250);}</script>
      </body></html>`);
    w.document.close();
  };
  return (
    <Dialog open={!!part} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-sm font-body text-center" dir="rtl" data-testid="qr-dialog">
        <DialogHeader><DialogTitle className="font-heading text-lg text-center">باركود القطعة</DialogTitle></DialogHeader>
        {dataUrl ? <img src={dataUrl} alt="qr" className="mx-auto h-56 w-56" data-testid="qr-image" /> : <Loader2 className="mx-auto h-8 w-8 animate-spin text-slate-300" />}
        <p className="font-heading font-bold text-slate-800 mt-1">{part?.name}</p>
        <p className="text-xs text-slate-400 font-mono-nums" dir="ltr">SKU: {part?.sku}</p>
        <p className="text-xs text-slate-400">امسح الرمز بكاميرا الجوال لتسجيل صرف القطعة على مركبة. يظهر الاسم في الملصق بالعربية والإنجليزية والأوردو.</p>
        <Button data-testid="qr-print-btn" disabled={printing} onClick={printLabel} className="gap-1.5 bg-slate-800 hover:bg-slate-900">{printing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Printer className="h-4 w-4" />} طباعة الملصق</Button>
      </DialogContent>
    </Dialog>
  );
};

export const SparePartsPanel = ({ canEdit }) => {
  const [tab, setTab] = useState("stock");
  const [data, setData] = useState(null);
  const [consumptions, setConsumptions] = useState(null);
  const [maint, setMaint] = useState(null);
  const [vehicles, setVehicles] = useState([]);
  const [search, setSearch] = useState("");
  const [vehFilter, setVehFilter] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [qrPart, setQrPart] = useState(null);
  const [restockPart, setRestockPart] = useState(null);
  const [transferPart, setTransferPart] = useState(null);
  const [importing, setImporting] = useState(false);
  const [warehouses, setWarehouses] = useState([]);
  const [whId, setWhId] = useState("");
  const [manageOpen, setManageOpen] = useState(false);
  const fileRef = useRef(null);

  const currentWh = warehouses.find((w) => w.id === whId);

  const loadWarehouses = useCallback(async () => {
    try {
      const r = await api.warehouses();
      const list = r.items || [];
      setWarehouses(list);
      setWhId((cur) => cur || (list.find((w) => w.is_main) || list[0] || {}).id || "");
    } catch { /* non-blocking */ }
  }, []);

  const onImport = async (e) => {
    const file = e.target.files?.[0]; e.target.value = "";
    if (!file) return;
    setImporting(true);
    try {
      const res = await api.importSpareParts(file, whId || undefined);
      toast.success(`تم الاستيراد — أُضيف ${res.created} وحُدّث ${res.updated}`);
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "تعذّر استيراد الملف"); }
    finally { setImporting(false); }
  };

  const load = useCallback(async () => {
    try {
      const [d, v] = await Promise.all([api.spareParts(whId || undefined), api.sparePartVehicles()]);
      setData(d); setVehicles(v.vehicles || []);
    } catch { toast.error("تعذّر تحميل المخزون"); }
  }, [whId]);
  const loadConsumptions = useCallback(async () => {
    try { setConsumptions((await api.partConsumptions({ ...(whId ? { warehouse_id: whId } : {}), ...(vehFilter ? { vehicle: vehFilter } : {}) })).items); }
    catch { toast.error("تعذّر تحميل السجل"); }
  }, [vehFilter, whId]);

  useEffect(() => { loadWarehouses(); }, [loadWarehouses]);
  useEffect(() => { if (whId) load(); }, [load, whId]);
  useEffect(() => { if (tab === "archive") loadConsumptions(); }, [tab, loadConsumptions]);
  useEffect(() => {
    if (tab !== "maintenance") return;
    (async () => {
      try {
        const rows = (await api.partConsumptions(whId ? { warehouse_id: whId } : {})).items;
        const byVeh = {};
        rows.forEach((c) => {
          const v = c.vehicle_number || "غير محدد";
          const g = byVeh[v] || (byVeh[v] = { vehicle: v, cost: 0, qty: 0, count: 0, parts: {} });
          g.cost += Number(c.unit_price || 0) * Number(c.quantity || 0);
          g.qty += Number(c.quantity || 0); g.count += 1;
          g.parts[c.part_name] = (g.parts[c.part_name] || 0) + Number(c.quantity || 0);
        });
        setMaint(Object.values(byVeh).map((g) => ({ ...g, cost: Math.round(g.cost * 100) / 100, partCount: Object.keys(g.parts).length })).sort((a, b) => b.cost - a.cost));
      } catch { toast.error("تعذّر تحميل التقرير"); }
    })();
  }, [tab, data, whId]);

  const del = async (p) => {
    if (!window.confirm(`حذف القطعة «${p.name}»؟`)) return;
    try { await api.deleteSparePart(p.id); toast.success("تم الحذف"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "تعذّر الحذف"); }
  };

  const items = (data?.items || []).filter((p) =>
    !search || p.name.includes(search) || (p.sku || "").toLowerCase().includes(search.toLowerCase()));

  return (
    <div className="space-y-4" data-testid="spare-parts-panel">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <h3 className="font-heading font-bold text-slate-800 flex items-center gap-2"><Package className="h-5 w-5 text-blue-600" /> مخزون قطع الغيار</h3>
          {data?.low_count > 0 && (
            <Badge variant="outline" className="rounded-full border-amber-200 bg-amber-50 text-amber-700 gap-1">
              <AlertTriangle className="h-3 w-3" /> {data.low_count} قطعة منخفضة
            </Badge>
          )}
        </div>
        {canEdit && (
          <div className="flex items-center gap-2 flex-wrap">
            <input ref={fileRef} type="file" accept=".xlsx,.xls" className="hidden" onChange={onImport} data-testid="parts-import-input" />
            <BulkTools section="spare_parts" onDone={load} />
            <Button variant="outline" data-testid="parts-import-btn" onClick={() => fileRef.current?.click()} disabled={importing} className="gap-1.5 h-9">
              {importing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} استيراد Excel
            </Button>
            <a href={api.sparePartsTemplateUrl()} data-testid="parts-template-link">
              <Button variant="outline" className="gap-1.5 h-9"><FileSpreadsheet className="h-4 w-4" /> القالب</Button>
            </a>
            <a href={api.sparePartsExportUrl()} data-testid="parts-export-link">
              <Button variant="outline" className="gap-1.5 h-9"><Download className="h-4 w-4" /> تنزيل Excel</Button>
            </a>
            <Button data-testid="add-part-btn" onClick={() => { setEditing(null); setDialogOpen(true); }} className="bg-blue-600 hover:bg-blue-700 gap-1.5 h-9">
              <Plus className="h-4 w-4" /> إضافة قطعة
            </Button>
          </div>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-2" data-testid="warehouse-bar">
        <Warehouse className="h-4 w-4 text-slate-400" />
        <select data-testid="warehouse-select" value={whId} onChange={(e) => setWhId(e.target.value)}
          className="h-9 rounded-md border border-slate-200 bg-white px-3 text-sm font-semibold text-slate-700 min-w-[180px]">
          {warehouses.map((w) => (
            <option key={w.id} value={w.id}>
              {w.is_main ? "🏢 " : "🏬 "}{w.name}{w.is_main ? " (الرئيسي)" : ""} — {w.part_count ?? 0} قطعة
            </option>
          ))}
        </select>
        {currentWh && !currentWh.is_main && (
          <Badge variant="outline" className="rounded-full border-blue-200 bg-blue-50 text-blue-700 text-[11px]">فرع — سحب فقط من بوابته</Badge>
        )}
        {canEdit && (
          <Button variant="outline" data-testid="manage-warehouses-btn" onClick={() => setManageOpen(true)} className="gap-1.5 h-9">
            <Settings2 className="h-4 w-4" /> إدارة المستودعات
          </Button>
        )}
      </div>

      <Tabs value={tab} onValueChange={setTab} dir="rtl">
        <TabsList data-testid="parts-tabs" className="w-full max-w-full overflow-x-auto flex-nowrap justify-start no-scrollbar">
          <TabsTrigger value="stock" data-testid="parts-tab-stock" className="shrink-0">المخزون</TabsTrigger>
          <TabsTrigger value="archive" data-testid="parts-tab-archive" className="shrink-0">سجل الاستهلاك</TabsTrigger>
          <TabsTrigger value="maintenance" data-testid="parts-tab-maintenance" className="shrink-0">تكلفة الصيانة</TabsTrigger>
        </TabsList>
      </Tabs>

      {tab === "maintenance" ? (
        <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white" data-testid="maintenance-report">
          <div className="min-w-[560px]">
            <div className="grid items-center grid-cols-[1.8fr_1fr_1.2fr_1fr_1.4fr] bg-slate-50 text-slate-500 text-xs">
              <div className="px-3 py-2.5 font-semibold text-right">المركبة</div>
              <div className="px-3 py-2.5 font-semibold text-center">عدد العمليات</div>
              <div className="px-3 py-2.5 font-semibold text-center">الكمية المستهلكة</div>
              <div className="px-3 py-2.5 font-semibold text-center">أنواع القطع</div>
              <div className="px-3 py-2.5 font-semibold text-center">إجمالي تكلفة الصيانة</div>
            </div>
            {!maint ? (
              <div className="px-3 py-10 text-center text-slate-300"><Loader2 className="mx-auto h-5 w-5 animate-spin" /></div>
            ) : maint.length === 0 ? (
              <div className="px-3 py-10 text-center text-slate-400">لا يوجد استهلاك بعد لحساب التكلفة.</div>
            ) : maint.map((g) => (
              <div key={g.vehicle} data-testid={`maint-row-${g.vehicle}`}
                className="grid items-center grid-cols-[1.8fr_1fr_1.2fr_1fr_1.4fr] border-t border-slate-100 hover:bg-slate-50/60 text-sm">
                <div className="px-3 py-2.5 text-right"><span className="inline-flex items-center gap-1 font-semibold text-slate-800 font-mono-nums"><Car className="h-4 w-4 text-slate-400" />{g.vehicle}</span></div>
                <div className="px-3 py-2.5 font-mono-nums text-slate-600 text-center">{g.count}</div>
                <div className="px-3 py-2.5 font-mono-nums text-slate-600 text-center">{g.qty}</div>
                <div className="px-3 py-2.5 font-mono-nums text-slate-600 text-center">{g.partCount}</div>
                <div className="px-3 py-2.5 font-mono-nums font-bold text-blue-700 text-center">{money(g.cost)}</div>
              </div>
            ))}
          </div>
        </div>
      ) : tab === "stock" ? (
        <>
          <div className="relative sm:w-72">
            <Search className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input data-testid="parts-search" placeholder="ابحث بالاسم أو الرقم..." value={search} onChange={(e) => setSearch(e.target.value)} className="pr-9" />
          </div>
          <div className="sm:hidden space-y-2" data-testid="parts-cards">
            {!data ? (
              <div className="py-10 text-center text-slate-300"><Loader2 className="mx-auto h-5 w-5 animate-spin" /></div>
            ) : items.length === 0 ? (
              <div className="py-10 text-center text-slate-400 text-sm">لا توجد قطع في المخزون بعد.</div>
            ) : items.map((p) => (
              <div key={p.id} data-testid={`part-card-${p.id}`} className="rounded-2xl border border-slate-200 bg-white p-3">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="font-semibold text-slate-800 truncate">{p.name}</p>
                    {p.sku && <p className="text-[11px] text-slate-400 font-mono-nums" dir="ltr">SKU: {p.sku}</p>}
                  </div>
                  <span className={`shrink-0 inline-flex items-center gap-1 font-mono-nums font-bold ${p.low_stock ? "text-amber-600" : "text-slate-700"}`}>{p.quantity}{p.low_stock && <AlertTriangle className="h-3.5 w-3.5" />}</span>
                </div>
                <div className="mt-2 grid grid-cols-3 gap-2 text-xs text-slate-500">
                  <span className="truncate">التصنيف: <b className="text-slate-700">{p.category || "—"}</b></span>
                  <span className="truncate">المكان: <b className="text-slate-700">{p.location || "—"}</b></span>
                  <span className="truncate">السعر: <b className="text-slate-700 font-mono-nums">{p.price ? money(p.price) : "—"}</b></span>
                </div>
                <div className="mt-2 flex items-center gap-1 border-t border-slate-100 pt-2">
                  <button data-testid={`part-qr-m-${p.id}`} onClick={() => setQrPart(p)} className="flex-1 inline-flex items-center justify-center gap-1 rounded-lg bg-blue-50 text-blue-600 py-1.5 text-xs font-medium"><QrCode className="h-4 w-4" /> باركود</button>
                  {canEdit && <button data-testid={`part-restock-m-${p.id}`} onClick={() => setRestockPart(p)} className="flex-1 inline-flex items-center justify-center gap-1 rounded-lg bg-emerald-50 text-emerald-600 py-1.5 text-xs font-medium"><PackagePlus className="h-4 w-4" /> إدخال</button>}
                  {canEdit && currentWh?.is_main && <button data-testid={`part-transfer-m-${p.id}`} onClick={() => setTransferPart(p)} className="flex-1 inline-flex items-center justify-center gap-1 rounded-lg bg-indigo-50 text-indigo-600 py-1.5 text-xs font-medium"><ArrowLeftRight className="h-4 w-4" /> تحويل</button>}
                  {canEdit && <button data-testid={`part-edit-m-${p.id}`} onClick={() => { setEditing(p); setDialogOpen(true); }} className="flex-1 inline-flex items-center justify-center gap-1 rounded-lg bg-slate-100 text-slate-600 py-1.5 text-xs font-medium"><Pencil className="h-4 w-4" /> تعديل</button>}
                  {canEdit && <button data-testid={`part-del-m-${p.id}`} onClick={() => del(p)} className="flex-1 inline-flex items-center justify-center gap-1 rounded-lg bg-rose-50 text-rose-600 py-1.5 text-xs font-medium"><Trash2 className="h-4 w-4" /> حذف</button>}
                </div>
              </div>
            ))}
          </div>
          <div className="hidden sm:block overflow-x-auto rounded-2xl border border-slate-200 bg-white">
            <table className="w-full min-w-[720px] text-sm text-right">
              <thead className="bg-slate-50 text-slate-500 text-xs">
                <tr>
                  <th className="px-3 py-2.5 font-semibold">القطعة</th>
                  <th className="px-3 py-2.5 font-semibold">SKU</th>
                  <th className="px-3 py-2.5 font-semibold">الكمية</th>
                  <th className="px-3 py-2.5 font-semibold">التصنيف</th>
                  <th className="px-3 py-2.5 font-semibold">المكان</th>
                  <th className="px-3 py-2.5 font-semibold">السعر</th>
                  <th className="px-3 py-2.5 font-semibold text-center">إجراءات</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {!data ? (
                  <tr><td colSpan={7} className="px-3 py-10 text-center text-slate-300"><Loader2 className="mx-auto h-5 w-5 animate-spin" /></td></tr>
                ) : items.length === 0 ? (
                  <tr><td colSpan={7} className="px-3 py-10 text-center text-slate-400">لا توجد قطع في المخزون بعد.</td></tr>
                ) : items.map((p) => (
                  <tr key={p.id} data-testid={`part-row-${p.id}`} className="hover:bg-slate-50/60">
                    <td className="px-3 py-2.5 font-semibold text-slate-800">{p.name}</td>
                    <td className="px-3 py-2.5 font-mono-nums text-slate-500" dir="ltr">{p.sku}</td>
                    <td className="px-3 py-2.5">
                      <span className={`inline-flex items-center gap-1 font-mono-nums font-bold ${p.low_stock ? "text-amber-600" : "text-slate-700"}`}>
                        {p.quantity}{p.low_stock && <AlertTriangle className="h-3.5 w-3.5" />}
                      </span>
                    </td>
                    <td className="px-3 py-2.5 text-slate-500">{p.category || "—"}</td>
                    <td className="px-3 py-2.5 text-slate-500">{p.location || "—"}</td>
                    <td className="px-3 py-2.5 text-slate-500 font-mono-nums">{p.price ? money(p.price) : "—"}</td>
                    <td className="px-3 py-2.5">
                      <div className="flex items-center justify-center gap-1">
                        <button data-testid={`part-qr-${p.id}`} title="باركود" onClick={() => setQrPart(p)} className="p-1.5 rounded-lg text-blue-600 hover:bg-blue-50"><QrCode className="h-4 w-4" /></button>
                        {canEdit && <button data-testid={`part-restock-${p.id}`} title="إدخال كمية" onClick={() => setRestockPart(p)} className="p-1.5 rounded-lg text-emerald-600 hover:bg-emerald-50"><PackagePlus className="h-4 w-4" /></button>}
                        {canEdit && currentWh?.is_main && <button data-testid={`part-transfer-${p.id}`} title="تحويل إلى فرع" onClick={() => setTransferPart(p)} className="p-1.5 rounded-lg text-indigo-600 hover:bg-indigo-50"><ArrowLeftRight className="h-4 w-4" /></button>}
                        {canEdit && <button data-testid={`part-edit-${p.id}`} title="تعديل" onClick={() => { setEditing(p); setDialogOpen(true); }} className="p-1.5 rounded-lg text-slate-500 hover:bg-slate-100"><Pencil className="h-4 w-4" /></button>}
                        {canEdit && <button data-testid={`part-del-${p.id}`} title="حذف" onClick={() => del(p)} className="p-1.5 rounded-lg text-rose-500 hover:bg-rose-50"><Trash2 className="h-4 w-4" /></button>}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      ) : (
        <>
          <div className="flex items-center gap-2 flex-wrap">
            <History className="h-4 w-4 text-slate-400" />
            <select data-testid="archive-vehicle-filter" value={vehFilter} onChange={(e) => setVehFilter(e.target.value)}
              className="h-9 rounded-md border border-slate-200 bg-white px-3 text-sm">
              <option value="">كل المركبات</option>
              {vehicles.map((v) => <option key={v} value={v}>{v}</option>)}
            </select>
          </div>
          <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white">
            <table className="w-full min-w-[720px] text-sm text-right">
              <thead className="bg-slate-50 text-slate-500 text-xs">
                <tr>
                  <th className="px-3 py-2.5 font-semibold">التاريخ</th>
                  <th className="px-3 py-2.5 font-semibold">القطعة</th>
                  <th className="px-3 py-2.5 font-semibold">الكمية</th>
                  <th className="px-3 py-2.5 font-semibold">المركبة</th>
                  <th className="px-3 py-2.5 font-semibold">بواسطة</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {!consumptions ? (
                  <tr><td colSpan={5} className="px-3 py-10 text-center text-slate-300"><Loader2 className="mx-auto h-5 w-5 animate-spin" /></td></tr>
                ) : consumptions.length === 0 ? (
                  <tr><td colSpan={5} className="px-3 py-10 text-center text-slate-400">لا يوجد سجل استهلاك.</td></tr>
                ) : consumptions.map((c) => (
                  <tr key={c.id} data-testid={`consumption-row-${c.id}`} className="hover:bg-slate-50/60">
                    <td className="px-3 py-2.5 text-slate-500 font-mono-nums" dir="ltr">{String(c.created_at).slice(0, 16).replace("T", " ")}</td>
                    <td className="px-3 py-2.5 font-semibold text-slate-800">{c.part_name} {c.sku ? <span className="text-xs text-slate-400 font-mono-nums" dir="ltr">({c.sku})</span> : <span className="rounded bg-amber-50 px-1.5 text-[10px] text-amber-700">تسجيل سريع</span>}</td>
                    <td className="px-3 py-2.5 font-mono-nums font-bold text-rose-600">{c.quantity}</td>
                    <td className="px-3 py-2.5"><span className="inline-flex items-center gap-1 whitespace-nowrap text-slate-700 font-mono-nums"><Car className="h-3.5 w-3.5 text-slate-400" />{c.vehicle_number}</span></td>
                    <td className="px-3 py-2.5 text-slate-500">{c.actor_name || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      <PartDialog open={dialogOpen} onOpenChange={setDialogOpen} initial={editing} onSaved={load} warehouseId={whId} />
      <QrDialog part={qrPart} onClose={() => setQrPart(null)} />
      <RestockDialog part={restockPart} onClose={() => setRestockPart(null)} onDone={load} />
      <TransferDialog part={transferPart} branches={warehouses.filter((w) => !w.is_main)} onClose={() => setTransferPart(null)} onDone={() => { load(); loadWarehouses(); }} />
      <WarehousesDialog open={manageOpen} onOpenChange={setManageOpen} onChanged={loadWarehouses} />
    </div>
  );
};
